/**
 * Hierarchy CRUD — rename, add, reorder, retire Goal Categories and
 * Goal Sub-categories.
 *
 * All operations are single-document writes (or two-doc batches for
 * reorder) against `_goalCategory` / `_goalSubcategory`. Goals and
 * in-flight care-plan Goal docs are untouched — they reference the
 * hierarchy by stable code per OMON-004 / ADR-0007. A rename is
 * visible across the entire app on next render with zero sweep.
 *
 * Includes `slugify` (helper used by saveNewCategory + saveNewSubcategory to
 * derive a code from a display name) and the toolbar/action binders
 * called by renderCatalogue.
 *
 * OMON-079 79h (2026-05-10): collection writes redirected from the
 * pre-reseed names (`_catalogueCategory` / `_catalogueAim`) to the
 * canonical post-reseed names. Internal symbols and field names
 * follow the same rename: `goalCategories` / `goalSubcategories`,
 * `GoalCategoryNode` / `GoalSubcategoryNode`, `goalCategoryCode`.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6c.3.
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import { state } from "../state";
import { createFact } from "./custom";
import { db } from "../firebase";
import { catalogueList, statusEl } from "../dom";
import type { GoalCategoryNode, GoalSubcategoryNode } from "../types";
import { renderCatalogue } from "./tree";
import { renameFact } from "./detail";
import { metaSecurityFor } from "../admin/meta-security";
import { slugify } from "./slug";
import type { ProvenanceFilter } from "./provenance-view";


// ======================================================================
// Hierarchy CRUD — rename, add, reorder, retire Goal Categories and
// Goal Sub-categories
// ======================================================================
//
// All operations are single-document writes (or two-doc batches for reorder)
// against _goalCategory / _goalSubcategory. Goal docs are untouched — they
// reference the hierarchy by stable code per OMON-004 / ADR-0007. A rename
// is visible across the entire app on next render with zero sweep.

// OMON-094 Phase 0: slugify moved to the shared `./slug.ts` module and
// switched to kebab-case to match reseed_v4 + post-OMON-079 catalogue
// shape. Re-exported here for backward compatibility with existing
// `import { slugify } from "./hierarchy"` paths.
export { slugify };

export function bindHierarchyToolbar() {
  catalogueList.querySelector("#toggleShowRetired")?.addEventListener("change", (e) => {
    state.hierarchyShowRetired = (e.target as HTMLInputElement).checked;
    renderCatalogue();
  });
  catalogueList.querySelector("#toggleShowOnlyPlaceholder")?.addEventListener("change", (e) => {
    state.hierarchyShowOnlyPlaceholder = (e.target as HTMLInputElement).checked;
    renderCatalogue();
  });
  // OMON-167: capability lens chips. Multi-select toggle into/out of the
  // session filter Set; empty Set = show all. "clear" resets the lens.
  catalogueList.querySelectorAll<HTMLElement>("[data-cap-filter]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      const cap = el.dataset.capFilter!;
      if (state.catalogueCapabilityFilter.has(cap)) {
        state.catalogueCapabilityFilter.delete(cap);
      } else {
        state.catalogueCapabilityFilter.add(cap);
      }
      renderCatalogue();
    });
  });
  catalogueList.querySelector("[data-cap-filter-clear]")?.addEventListener("click", (e) => {
    e.stopPropagation();
    state.catalogueCapabilityFilter.clear();
    renderCatalogue();
  });
  // ADR-0048 phase 2: the provenance lens. Single-select, unlike the
  // capability chips above — "authored in training" and "authored here" are
  // exhaustive and mutually exclusive, so a multi-select would only ever
  // mean "all" by a longer route.
  catalogueList.querySelectorAll<HTMLElement>("[data-prov-filter]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      state.catalogueProvenanceFilter = el.dataset.provFilter as ProvenanceFilter;
      renderCatalogue();
    });
  });
}

export function bindHierarchyActions() {
  catalogueList.querySelectorAll<HTMLElement>("[data-hier-action]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      const action = el.dataset.hierAction!;
      const code = el.dataset.code;
      const catCode = el.dataset.catCode;

      switch (action) {
        case "rename-cat":
          state.hierarchyInlineEdit = { kind: "category", code: code! };
          renderCatalogue();
          break;
        case "rename-aim":
          state.hierarchyInlineEdit = { kind: "aim", code: code!, categoryCode: catCode! };
          renderCatalogue();
          break;
        case "up-cat":
          swapCategoryOrder(code!, -1);
          break;
        case "down-cat":
          swapCategoryOrder(code!, +1);
          break;
        case "up-aim":
          swapSubcategoryOrder(catCode!, code!, -1);
          break;
        case "down-aim":
          swapSubcategoryOrder(catCode!, code!, +1);
          break;
        case "retire-cat":
          setCategoryStatus(code!, "retired");
          break;
        case "restore-cat":
          setCategoryStatus(code!, "active");
          break;
        case "retire-aim":
          setSubcategoryStatus(catCode!, code!, "retired");
          break;
        case "restore-aim":
          setSubcategoryStatus(catCode!, code!, "active");
          break;
        case "show-new-category":
          state.hierarchyShowAddCategory = true;
          renderCatalogue();
          break;
        case "cancel-new-category":
          state.hierarchyShowAddCategory = false;
          renderCatalogue();
          break;
        case "save-new-category": {
          const input = catalogueList.querySelector<HTMLInputElement>("#newCategoryNameInput");
          if (input) saveNewCategory(input.value);
          break;
        }
        case "show-new-aim":
          state.hierarchyAddSubcategoryIn = catCode!;
          renderCatalogue();
          break;
        case "cancel-new-aim":
          state.hierarchyAddSubcategoryIn = null;
          renderCatalogue();
          break;
        case "save-new-aim": {
          const input = catalogueList.querySelector<HTMLInputElement>("#newAimNameInput");
          if (input) saveNewSubcategory(catCode!, input.value);
          break;
        }
        case "cancel-new-fact":
          state.hierarchyAddFactIn = null;
          renderCatalogue();
          break;
        case "save-new-fact": {
          const input = catalogueList.querySelector<HTMLInputElement>("#newFactNameInput");
          if (input) {
            void createFact(catCode!, input.value).then((ok) => {
              if (ok) {
                state.hierarchyAddFactIn = null;
                renderCatalogue();
              }
            });
          }
          break;
        }
      }
    });
  });
}

export async function commitInlineRename(input: HTMLInputElement) {
  const kind = input.dataset.renameKind as "category" | "aim" | "fact" | undefined;
  const code = input.dataset.renameCode;
  const catCode = input.dataset.renameCatCode;
  const newDisplay = input.value.trim();

  if (!kind || !code || !newDisplay) {
    state.hierarchyInlineEdit = null;
    renderCatalogue();
    return;
  }

  // OMON-166 Part 4: fact rename delegates to the OD+AD title writer in
  // detail.ts (category/aim renames are _goalCategory/_goalSubcategory writes).
  if (kind === "fact") {
    await renameFact(code, newDisplay);
    state.hierarchyInlineEdit = null;
    renderCatalogue();
    return;
  }

  try {
    if (kind === "category") {
      const node = state.goalCategories.get(code);
      if (!node || node.display === newDisplay) {
        state.hierarchyInlineEdit = null;
        renderCatalogue();
        return;
      }
      await updateDoc(doc(db, "_goalCategory", code), { display: newDisplay });
      node.display = newDisplay;
      statusEl.textContent = `Renamed category to "${newDisplay}"`;
    } else {
      if (!catCode) return;
      const aimDocId = `${catCode}__${code}`;
      const node = state.goalSubcategories.get(aimDocId);
      if (!node || node.display === newDisplay) {
        state.hierarchyInlineEdit = null;
        renderCatalogue();
        return;
      }
      await updateDoc(doc(db, "_goalSubcategory", aimDocId), { display: newDisplay });
      node.display = newDisplay;
      statusEl.textContent = `Renamed sub-category to "${newDisplay}"`;
    }
  } catch (err) {
    console.error("Rename failed:", err);
    statusEl.textContent = "Rename failed — check console";
  }
  state.hierarchyInlineEdit = null;
  renderCatalogue();
}

export async function swapCategoryOrder(code: string, direction: -1 | 1) {
  const sorted = [...state.goalCategories.values()].sort(
    (a, b) => a.order - b.order || a.display.localeCompare(b.display),
  );
  const idx = sorted.findIndex((c) => c.code === code);
  if (idx < 0) return;
  const other = sorted[idx + direction];
  if (!other) return;
  const me = sorted[idx];

  try {
    const batch = writeBatch(db);
    batch.update(doc(db, "_goalCategory", me.code), { order: other.order });
    batch.update(doc(db, "_goalCategory", other.code), { order: me.order });
    await batch.commit();
    const tmp = me.order;
    me.order = other.order;
    other.order = tmp;
    renderCatalogue();
  } catch (err) {
    console.error("Reorder failed:", err);
    statusEl.textContent = "Reorder failed — check console";
  }
}

export async function swapSubcategoryOrder(categoryCode: string, subcategoryCode: string, direction: -1 | 1) {
  const siblings = [...state.goalSubcategories.values()]
    .filter((a) => a.goalCategoryCode === categoryCode)
    .sort((a, b) => a.order - b.order || a.display.localeCompare(b.display));
  const idx = siblings.findIndex((a) => a.code === subcategoryCode);
  if (idx < 0) return;
  const other = siblings[idx + direction];
  if (!other) return;
  const me = siblings[idx];
  const myDocId = `${categoryCode}__${me.code}`;
  const otherDocId = `${categoryCode}__${other.code}`;

  try {
    const batch = writeBatch(db);
    batch.update(doc(db, "_goalSubcategory", myDocId), { order: other.order });
    batch.update(doc(db, "_goalSubcategory", otherDocId), { order: me.order });
    await batch.commit();
    const tmp = me.order;
    me.order = other.order;
    other.order = tmp;
    renderCatalogue();
  } catch (err) {
    console.error("Reorder failed:", err);
    statusEl.textContent = "Reorder failed — check console";
  }
}

export async function setCategoryStatus(code: string, status: "active" | "retired") {
  const node = state.goalCategories.get(code);
  if (!node) return;
  try {
    // Canonical liveness field is `active` (what every loader reads); the old
    // `status` write went to a field nothing read, so retire never persisted.
    await updateDoc(doc(db, "_goalCategory", code), { active: status === "active" });
    node.status = status;
    statusEl.textContent = status === "retired"
      ? `Retired category "${node.display}" — objectives remain but are hidden from pickers.`
      : `Restored category "${node.display}"`;
    renderCatalogue();
  } catch (err) {
    console.error("Status change failed:", err);
    statusEl.textContent = "Status change failed — check console";
  }
}

export async function setSubcategoryStatus(categoryCode: string, subcategoryCode: string, status: "active" | "retired") {
  const aimDocId = `${categoryCode}__${subcategoryCode}`;
  const node = state.goalSubcategories.get(aimDocId);
  if (!node) return;
  try {
    // Canonical liveness field is `active` (what every loader reads); writing
    // `status` (the old behaviour) went unread, so retire never persisted.
    await updateDoc(doc(db, "_goalSubcategory", aimDocId), { active: status === "active" });
    node.status = status;
    statusEl.textContent = status === "retired"
      ? `Retired sub-category "${node.display}"`
      : `Restored sub-category "${node.display}"`;
    renderCatalogue();
  } catch (err) {
    console.error("Status change failed:", err);
    statusEl.textContent = "Status change failed — check console";
  }
}

/**
 * Pure write + state-update for a new Goal Category. Returns the new
 * node on success, null on validation failure or write error. Status
 * messages are written to `statusEl`. Does NOT trigger any catalogue
 * re-render — callers are responsible for that. Surfaced for the
 * librarian's "Add Custom Goal" controlled-vocabulary inline-create
 * flow (OMON-079 79h Half 2), in addition to the in-tree
 * `saveNewCategory` wrapper used by Manage Hierarchy.
 *
 * If the slugged code collides with an existing Goal Category, returns
 * the existing node — caller can decide whether that's a soft "you
 * already have this" or a hard error.
 */
export async function createGoalCategoryDoc(
  displayName: string,
): Promise<GoalCategoryNode | null> {
  const display = displayName.trim();
  if (!display) {
    statusEl.textContent = "Goal Category name cannot be empty.";
    return null;
  }
  // OMON-192: opaque id for new categories (was slugify(display)) — identity must
  // never be a fossil of the name, and must match the now-opaque existing
  // categories. Dedup on the human display, not a slug collision.
  const dup = [...state.goalCategories.values()].find(
    (c) => c.display.trim().toLowerCase() === display.toLowerCase(),
  );
  if (dup) {
    statusEl.textContent = `Goal Category "${dup.display}" already exists — using that.`;
    return dup;
  }
  const code = doc(collection(db, "_goalCategory")).id;
  const maxOrder = [...state.goalCategories.values()].reduce((m, c) => Math.max(m, c.order), 0);
  const node: GoalCategoryNode = {
    code,
    display,
    order: maxOrder + 1,
    status: "active",
  };
  const docData = {
    ...node,
    active: true,
    _sourceApplication: {
      _type: "reference",
      reference: "_actorDefinition/outcomemonitoring_web",
    },
    // OMON-111: meta.security stamped from the writer's dataScope claim.
    // `test` scope → HTEST label; `real`/`both`/nil → empty array.
    meta: {
      source: "https://sweettreeconnect.com/fhir/ActorDefinition/outcomemonitoring_web",
      security: metaSecurityFor(state.authClaims),
    },
  };
  try {
    await setDoc(doc(db, "_goalCategory", code), docData);
    state.goalCategories.set(code, node);
    statusEl.textContent = `Added Goal Category "${display}"`;
    return node;
  } catch (err) {
    console.error("Add Goal Category failed:", err);
    statusEl.textContent = "Add Goal Category failed — check console";
    return null;
  }
}

/**
 * Pure write + state-update for a new Goal Sub-category under
 * `categoryCode`. Same return semantics as `createGoalCategoryDoc`.
 * Surfaced for the same Add Custom Goal flow (OMON-079 79h Half 2).
 */
export async function createGoalSubcategoryDoc(
  categoryCode: string,
  displayName: string,
): Promise<GoalSubcategoryNode | null> {
  const display = displayName.trim();
  if (!display) {
    statusEl.textContent = "Goal Sub-category name cannot be empty.";
    return null;
  }
  // OMON-192: opaque bare sub-code (was slugify(display)). The composite doc id
  // stays {categoryCode}__{code} so the hierarchy lookup is unchanged; categoryCode
  // is itself opaque now. Dedup on display WITHIN this category, not a slug.
  const dup = [...state.goalSubcategories.values()].find(
    (s) => s.goalCategoryCode === categoryCode && s.display.trim().toLowerCase() === display.toLowerCase(),
  );
  if (dup) {
    statusEl.textContent = `Goal Sub-category "${dup.display}" already exists — using that.`;
    return dup;
  }
  const code = doc(collection(db, "_goalSubcategory")).id;
  const docId = `${categoryCode}__${code}`;
  const siblings = [...state.goalSubcategories.values()].filter((a) => a.goalCategoryCode === categoryCode);
  const maxOrder = siblings.reduce((m, a) => Math.max(m, a.order), 0);
  const node: GoalSubcategoryNode = {
    code,
    goalCategoryCode: categoryCode,
    display,
    order: maxOrder + 1,
    status: "active",
  };
  const docData = {
    code,
    category: {
      _type: "reference",
      reference: `_goalCategory/${categoryCode}`,
    },
    goalCategoryCode: categoryCode,
    display,
    order: maxOrder + 1,
    status: "active",
    active: true,
    _sourceApplication: {
      _type: "reference",
      reference: "_actorDefinition/outcomemonitoring_web",
    },
    // OMON-111: meta.security stamped from writer's dataScope claim.
    meta: {
      source: "https://sweettreeconnect.com/fhir/ActorDefinition/outcomemonitoring_web",
      security: metaSecurityFor(state.authClaims),
    },
  };
  try {
    await setDoc(doc(db, "_goalSubcategory", docId), docData);
    state.goalSubcategories.set(docId, node);
    statusEl.textContent = `Added Goal Sub-category "${display}"`;
    return node;
  } catch (err) {
    console.error("Add Goal Sub-category failed:", err);
    statusEl.textContent = "Add Goal Sub-category failed — check console";
    return null;
  }
}

export async function saveNewCategory(displayName: string) {
  const result = await createGoalCategoryDoc(displayName);
  if (result) {
    state.hierarchyShowAddCategory = false;
    renderCatalogue();
  }
}

export async function saveNewSubcategory(categoryCode: string, displayName: string) {
  const result = await createGoalSubcategoryDoc(categoryCode, displayName);
  if (result) {
    state.hierarchyAddSubcategoryIn = null;
    renderCatalogue();
  }
}

